/**
 * Bundle 75 Task 2 - "What's new" release-notes popover.
 *
 * Wraps the footer bundle chip. Fetches `/api/build-info/release-notes`,
 * shows the latest N entries in a shadcn Popover, and displays a
 * subtle pulse-dot when the current bundle is newer than the value
 * saved in localStorage. Opening the popover clears the dot.
 *
 * No em/en dashes, no eslint-disable.
 */
import React, { useEffect, useState } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { ChevronDown, Sparkles } from 'lucide-react';
import api from '@/utils/api';

const STORAGE_KEY = 'dostination.last_seen_bundle';
const READ_LIMIT = 12;

const ReleaseEntry = ({ entry }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-neutral-100 last:border-b-0 py-2" data-testid={`whatsnew-entry-${entry.bundle}`}>
      <button
        type="button"
        className="w-full text-left flex items-start justify-between gap-2 hover:bg-neutral-50 rounded px-1 py-1"
        onClick={() => setOpen((v) => !v)}
        data-testid={`whatsnew-toggle-${entry.bundle}`}
      >
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 text-[11px] text-neutral-500">
            <span className="font-mono font-semibold text-primary-900">Bundle {entry.bundle}</span>
            <span aria-hidden="true">*</span>
            <span>{entry.ship_date}</span>
          </div>
          <div className="text-xs text-primary-900 font-medium leading-snug">{entry.headline}</div>
          {!open && entry.summary ? (
            <div className="text-[11px] text-neutral-600 mt-0.5 truncate">{entry.summary}</div>
          ) : null}
        </div>
        <ChevronDown className={`h-3.5 w-3.5 text-neutral-400 mt-1 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open ? (
        <pre
          className="text-[11px] text-primary-700 whitespace-pre-wrap font-sans mt-1 px-1 leading-relaxed"
          data-testid={`whatsnew-body-${entry.bundle}`}
        >{entry.notes_md}</pre>
      ) : null}
    </div>
  );
};

const WhatsNewPopover = ({ highestBundle }) => {
  const [notes, setNotes] = useState(null);
  const [busy, setBusy] = useState(false);
  const [hasNew, setHasNew] = useState(false);

  useEffect(() => {
    let mounted = true;
    setBusy(true);
    api.get('/build-info/release-notes')
      .then((r) => {
        if (!mounted) return;
        setNotes(r.data);
        const current = r.data?.current_bundle || highestBundle || 0;
        let seen = 0;
        try {
          seen = parseInt(window.localStorage.getItem(STORAGE_KEY) || '0', 10) || 0;
        } catch (e) { seen = 0; }
        setHasNew(current > seen);
      })
      .catch(() => {})
      .finally(() => { if (mounted) setBusy(false); });
    return () => { mounted = false; };
  }, [highestBundle]);

  const onOpen = (open) => {
    if (open && notes?.current_bundle) {
      try {
        window.localStorage.setItem(STORAGE_KEY, String(notes.current_bundle));
      } catch (e) { /* ignore quota errors */ }
      setHasNew(false);
    }
  };

  return (
    <Popover onOpenChange={onOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="relative inline-flex items-center gap-1 hover:text-primary-900 text-xs cursor-pointer"
          data-testid="admin-footer-bundle-chip"
          title="See what's new"
        >
          <span data-testid="admin-footer-bundle-label">Bundle {highestBundle ?? '?'}</span>
          {hasNew ? (
            <span
              className="absolute -top-1 -right-2 h-2 w-2 rounded-full bg-red-500 ring-2 ring-white animate-pulse"
              data-testid="admin-footer-bundle-new-dot"
              aria-hidden="true"
            />
          ) : null}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="end" data-testid="whatsnew-popover">
        <div className="p-3 border-b border-neutral-100 bg-neutral-50">
          <div className="flex items-center gap-2 text-secondary-800 text-[10px] uppercase tracking-widest">
            <Sparkles className="h-3 w-3" /> What&apos;s new
          </div>
          <div className="text-xs text-primary-900 mt-1">
            {busy ? 'Loading...' : `Showing latest ${Math.min(READ_LIMIT, notes?.entries?.length || 0)} of ${notes?.count || 0} bundles`}
          </div>
        </div>
        <div className="max-h-96 overflow-y-auto px-2">
          {(notes?.entries || []).slice(0, READ_LIMIT).map((e) => (
            <ReleaseEntry key={e.bundle} entry={e} />
          ))}
        </div>
        <div className="p-2 border-t border-neutral-100 text-[10px] text-neutral-500 text-center">
          For the full history, see /app/memory/release_notes.md
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default WhatsNewPopover;
